<template>
    <div
        class="fixed-checkout-cta"
        :class="{ 'fixed-checkout-cta--visible': visible }"
        :aria-hidden="visible ? 'false' : 'true'"
    >
        <button
            type="button"
            class="fixed-checkout-cta__button"
            data-fixed-checkout-cta
            :tabindex="visible ? undefined : -1"
            @click="$emit('activate')"
        >
            {{ label }}
        </button>
    </div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref } from "vue";

/**
 * The page-level CTA that opens the checkout modal — the SalesByte 2.0 design
 * (Johansalesbyte/Salesbyte-2.0-CLAUDE, ECP2 Index.tsx "Fixed Bottom CTA"):
 * a large fixed bar pinned to the bottom of the viewport that slides in once
 * the customer has scrolled past the packages/options section, so the offer
 * is read before the ask appears.
 *
 * Only the shape and behaviour come from the reference. The colors stay the
 * merchant's: the button's background/text are painted by the same
 * profile-color style block in quotes/view.blade.php that paints every other
 * sign button, which also supplies the --cta-glow custom property the hover
 * glow derives from.
 */
const props = defineProps({
    label: { type: String, required: true },
    /**
     * CSS selector for the section the customer must scroll past before the
     * CTA appears (the reference's packagesRef). When the page cannot scroll
     * at all — a one-package quote with no overflow — the CTA shows anyway,
     * because "past the packages" is unreachable on a page with no scroll.
     */
    anchor: { type: String, default: "[data-cta-anchor]" },
});

defineEmits(["activate"]);

const visible = ref(false);

function evaluate() {
    const el = document.querySelector(props.anchor);
    if (!el) {
        // No packages section on the page (e.g. the carousel is disabled):
        // there is nothing to scroll past, so the ask may show at once.
        visible.value = true;
        return;
    }
    const rect = el.getBoundingClientRect();
    visible.value =
        rect.bottom < window.innerHeight - 200 ||
        document.documentElement.scrollHeight <= window.innerHeight + 4;
}

onMounted(() => {
    window.addEventListener("scroll", evaluate, { passive: true });
    window.addEventListener("resize", evaluate, { passive: true });
    evaluate();
});

onBeforeUnmount(() => {
    window.removeEventListener("scroll", evaluate);
    window.removeEventListener("resize", evaluate);
});
</script>

<style scoped>
/* Below Bootstrap's modal stack (1040/1050), so the checkout overlays it. */
.fixed-checkout-cta {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1030;
    display: flex;
    justify-content: center;
    padding: 10px 16px 32px;
    pointer-events: none;
    opacity: 0;
    transform: translateY(16px);
    transition: opacity 0.3s ease, transform 0.3s ease;
}

.fixed-checkout-cta--visible {
    opacity: 1;
    transform: none;
}

.fixed-checkout-cta__button {
    pointer-events: none;
    width: 70%;
    max-width: 520px;
    border: 0;
    border-radius: 12px;
    padding: 2.25rem 2rem;
    font-size: clamp(1.375rem, 4.5vw, 1.875rem);
    font-weight: 600;
    line-height: 1.2;
    cursor: pointer;
    box-shadow: 0 20px 50px -10px hsla(220, 30%, 15%, 0.15);
    transition: transform 0.3s ease, box-shadow 0.3s ease, filter 0.3s ease;
}

.fixed-checkout-cta--visible .fixed-checkout-cta__button {
    pointer-events: auto;
}

.fixed-checkout-cta__button:hover {
    transform: scale(1.02);
    filter: brightness(0.95);
    /* The plain declaration first: browsers without color-mix keep a lifted
       shadow, ones with it add the profile-colored glow on top. */
    box-shadow: 0 20px 50px -10px hsla(220, 30%, 15%, 0.25);
    box-shadow:
        0 20px 50px -10px hsla(220, 30%, 15%, 0.25),
        0 0 40px color-mix(in srgb, var(--cta-glow, transparent) 50%, transparent);
}

.fixed-checkout-cta__button:focus-visible {
    outline: 3px solid currentColor;
    outline-offset: 3px;
}
</style>
